import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ShieldWarning, PhoneCall, PaperPlaneTilt, Share, Warning, StopCircle, ChatCircle, Lifebuoy } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";

export default function SafetySheet() {
  const { bid } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [booking, setBooking] = useState(null);
  const [guidance, setGuidance] = useState(null);
  const [reportKind, setReportKind] = useState(null); // 'service_dispute' | 'safety_incident'
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [severity, setSeverity] = useState("medium");

  useEffect(() => {
    api.get("/safety/guidance").then((r) => setGuidance(r.data));
    if (bid) api.get(`/bookings/${bid}`).then((r) => setBooking(r.data)).catch(() => {});
  }, [bid]);

  const share = async () => {
    try {
      const { data } = await api.post(`/bookings/${bid}/share`);
      const text = `${data.message}\nProfessional: ${data.professional_name}\nArea: ${data.area}, ${data.city}${data.exact_address ? "\nAddress: " + data.exact_address : ""}`;
      if (navigator.share) await navigator.share({ title: "KUTZ Share", text });
      else { await navigator.clipboard.writeText(text); toast.success("Share text copied to clipboard."); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Share failed"); }
  };

  const submitReport = async () => {
    if (!subject.trim() || !description.trim()) { toast.error("Subject and description required"); return; }
    try {
      await api.post("/safety/reports", {
        booking_id: bid, kind: reportKind, subject, description, severity,
      });
      toast.success(reportKind === "safety_incident" ? "Safety incident escalated to KUTZ." : "Service concern logged.");
      setReportKind(null); setSubject(""); setDescription(""); setSeverity("medium");
    } catch (e) { toast.error(e?.response?.data?.detail || "Report failed"); }
  };

  const endEarly = async () => {
    if (!confirm("End this appointment early? A 6-hour service-dispute window will open.")) return;
    const reason = prompt("Reason (short, kept between you and KUTZ):") || "No reason given";
    try {
      await api.post(`/bookings/${bid}/end_early`, { reason });
      toast.success("Appointment ended. Dispute window opened.");
      nav("/upcoming");
    } catch (e) { toast.error(e?.response?.data?.detail || "End-early failed"); }
  };

  const contactSupport = async () => {
    const message = prompt("Tell KUTZ what's going on (kept private):");
    if (!message) return;
    try {
      await api.post("/support/tickets", { subject: "Safety follow-up", message, booking_id: bid });
      toast.success("Support ticket opened. KUTZ will be in touch.");
    } catch (e) { toast.error("Ticket failed"); }
  };

  if (!user) return <section className="max-w-md mx-auto px-6 py-24 text-center"><Link to="/auth" className="btn-gold inline-block px-6 py-3 rounded-full text-xs uppercase tracking-[0.25em] font-semibold">Sign in</Link></section>;

  return (
    <div className="max-w-2xl mx-auto px-5 pt-8 pb-24" data-testid="safety-sheet">
      <p className="overline mb-2 flex items-center gap-2 justify-start"><ShieldWarning size={12} weight="fill" color="#C5A059" /> KUTZ Safety</p>
      <h1 className="font-display text-4xl md:text-5xl tracking-tighter text-cream">You're not alone.</h1>
      <p className="text-cream/70 mt-3 text-sm">Serious safety concerns are <b>never</b> limited by the 6-hour service-dispute window. Everything below is private to KUTZ Safety.</p>

      {guidance && (
        <div className="mt-6 border border-red-400/40 bg-red-400/5 p-5" data-testid="safety-emergency">
          <p className="overline text-red-300">Emergency ({guidance.country})</p>
          <p className="text-cream mt-2">{guidance.text}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <a href={`tel:${guidance.emergency_number}`} data-testid="safety-call-000" className="btn-gold px-4 py-2 rounded-full text-[10px] uppercase tracking-[0.2em] font-semibold inline-flex items-center gap-1"><PhoneCall size={12} /> Call {guidance.emergency_number}</a>
            <a href={`tel:${guidance.kutz_hotline}`} data-testid="safety-call-kutz" className="btn-ghost px-4 py-2 rounded-full text-[10px] uppercase tracking-[0.2em] inline-flex items-center gap-1"><Lifebuoy size={12} /> KUTZ hotline</a>
          </div>
        </div>
      )}

      <div className="mt-6 grid gap-3" data-testid="safety-actions">
        {bid && (
          <button data-testid="safety-share" onClick={share} className="border border-cream/15 p-4 text-left hover:border-gold flex items-center gap-3">
            <Share size={20} color="#C5A059" /> <div><p className="text-cream text-sm">Share booking</p><p className="text-[10px] uppercase tracking-[0.2em] text-cream/50">Send trusted contact your appointment</p></div>
          </button>
        )}

        <button data-testid="safety-report-service" onClick={() => setReportKind("service_dispute")} className={`border p-4 text-left flex items-center gap-3 ${reportKind === "service_dispute" ? "border-gold bg-gold/5" : "border-cream/15 hover:border-cream/30"}`}>
          <Warning size={20} color="#C5A059" /> <div><p className="text-cream text-sm">Report a service concern</p><p className="text-[10px] uppercase tracking-[0.2em] text-cream/50">Dispute the cut, price, timing — within 6-hour window</p></div>
        </button>

        <button data-testid="safety-report-incident" onClick={() => setReportKind("safety_incident")} className={`border p-4 text-left flex items-center gap-3 ${reportKind === "safety_incident" ? "border-red-400 bg-red-400/5" : "border-red-400/30 hover:border-red-400"}`}>
          <ShieldWarning size={20} color="#F87171" /> <div><p className="text-red-200 text-sm">Report a safety incident</p><p className="text-[10px] uppercase tracking-[0.2em] text-red-300/70">Escalates to KUTZ Safety — no time limit</p></div>
        </button>

        {reportKind && (
          <div className="border border-cream/15 p-5 space-y-3" data-testid="safety-report-form">
            <p className="overline">{reportKind === "safety_incident" ? "Safety incident" : "Service concern"}</p>
            <input type="text" placeholder="Subject" data-testid="report-subject" value={subject} onChange={(e) => setSubject(e.target.value)} className="w-full bg-transparent border border-cream/15 p-2 text-cream text-sm" />
            <textarea rows={4} placeholder="Describe what happened" data-testid="report-description" value={description} onChange={(e) => setDescription(e.target.value)} className="w-full bg-transparent border border-cream/15 p-2 text-cream text-sm" />
            <div className="flex gap-2">
              {["low", "medium", "high"].map((s) => (
                <button key={s} data-testid={`sev-${s}`} onClick={() => setSeverity(s)} className={`chip text-[10px] ${severity === s ? "border-gold bg-gold/5 text-gold" : ""}`}>{s}</button>
              ))}
            </div>
            <div className="flex gap-2">
              <button data-testid="report-submit" onClick={submitReport} className="btn-gold px-4 py-2 rounded-full text-[10px] uppercase tracking-[0.2em] font-semibold inline-flex items-center gap-1"><PaperPlaneTilt size={12} /> Send report</button>
              <button data-testid="report-cancel" onClick={() => setReportKind(null)} className="btn-ghost px-4 py-2 rounded-full text-[10px] uppercase tracking-[0.2em]">Cancel</button>
            </div>
          </div>
        )}

        {bid && booking && !["completed", "ended_early"].includes(booking.status) && (
          <button data-testid="safety-end-early" onClick={endEarly} className="border border-red-400/30 hover:border-red-400 p-4 text-left flex items-center gap-3">
            <StopCircle size={20} color="#F87171" /> <div><p className="text-red-200 text-sm">End appointment early</p><p className="text-[10px] uppercase tracking-[0.2em] text-red-300/70">Opens the 6-hour service-dispute window immediately</p></div>
          </button>
        )}

        <button data-testid="safety-support" onClick={contactSupport} className="border border-cream/15 hover:border-cream/30 p-4 text-left flex items-center gap-3">
          <ChatCircle size={20} color="#C5A059" /> <div><p className="text-cream text-sm">Contact KUTZ Support</p><p className="text-[10px] uppercase tracking-[0.2em] text-cream/50">We'll reach out privately</p></div>
        </button>
      </div>
    </div>
  );
}
